import * as THREE from 'three';

import { Environment } from './Environment.js';
import { Church } from './Church.js';
import { Lighting } from './Lighting.js';

const DEFAULT_TELEPORT_CLEARANCE = 0.35;

/**
 * Owns the complete world layer and provides the navigation contract consumed
 * by XR locomotion. Construction is synchronous; initialize() resolves after
 * the GLB or its architectural fallback is ready.
 */
export class SceneManager {
  constructor(renderer, {
    quality = 'MEDIUM',
    loadingManager = THREE.DefaultLoadingManager,
  } = {}) {
    this.renderer = renderer;
    this.quality = quality;
    this.loadingManager = loadingManager;
    this.scene = new THREE.Scene();
    this.scene.name = 'rivero-exterior';

    this.environment = new Environment({ quality });
    this.church = new Church({ renderer, loadingManager, quality });
    this.lighting = new Lighting(this.scene, renderer, { quality });
    this.scene.add(this.environment.group, this.church.group);

    this.teleportableMeshes = this.environment.teleportableMeshes;
    this.blockedMeshes = this.church.blockedMeshes;
    this.walkableBounds = this.environment.walkableBounds;
    this.blockedBounds = [this.church.blockedBounds];
    this.ready = false;
    this.modelStatus = null;
    this._initializationPromise = null;
  }

  initialize() {
    if (!this._initializationPromise) {
      this._initializationPromise = this.church.load().then((modelStatus) => {
        this.modelStatus = modelStatus;
        this.ready = true;
        return this;
      });
    }
    return this._initializationPromise;
  }

  isTeleportDestinationValid(point, clearance = DEFAULT_TELEPORT_CLEARANCE) {
    if (
      !point
      || !Number.isFinite(point.x)
      || !Number.isFinite(point.y)
      || !Number.isFinite(point.z)
    ) {
      return false;
    }

    return (
      this.environment.isPointWithinWalkableBounds(point, clearance)
      && !this.church.containsHorizontalPoint(point, clearance)
    );
  }

  getNavigationData() {
    return {
      teleportableMeshes: this.teleportableMeshes,
      blockedMeshes: this.blockedMeshes,
      walkableBounds: this.walkableBounds,
      blockedBounds: this.blockedBounds,
      isDestinationValid: (point, clearance) => (
        this.isTeleportDestinationValid(point, clearance)
      ),
    };
  }

  dispose() {
    this.church.dispose();
    this.environment.dispose();
    this.lighting.dispose();
    this.scene.clear();
    this.teleportableMeshes.length = 0;
    this.blockedMeshes.length = 0;
    this.blockedBounds.length = 0;
    this.ready = false;
  }
}

export default SceneManager;
